import {
  DatePickerField,
  Form,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { isRS } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  BudgetTypeFormSchema,
  BudgetTypeLabelModifier,
  budgetType as budgetTypeEnum,
} from '../../models';
import { BUDGET_TYPE_EXTERNAL_SYSTEM_KEY_MAX_LENGTH } from '../../utils';

type FormValues = z.infer<typeof BudgetTypeFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const BudgetTypeForm: FC<Props> = ({
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
    setValue,
    resetField,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(BudgetTypeFormSchema),
  });

  const budgetType = watch('type');

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <DatePickerField
        name="fiscalYear"
        label={t('fiscal-year')}
        control={control}
        errors={errors}
        picker="year"
        required
        disabled={type === 'edit'}
      />

      <SelectField
        name="type"
        label={t('budget-type.master-table-title-budget-type')}
        control={control}
        options={budgetTypeEnum.options
          .filter((value) => {
            if (isRS()) {
              return [
                'initial',
                'supplementary',
                'carried-forward',
                'reserve',
              ].includes(value);
            }
            return !['reserve', 'carried-forward'].includes(value);
          })
          .map((value) => ({
            value,
            label: BudgetTypeLabelModifier[value],
          }))}
        errors={errors}
        shouldCoexistOnChange
        onChange={(value) => {
          if (value === 'initial') {
            setValue('name', t('initial-budget'));
          } else {
            resetField('name', { defaultValue: null as any });
          }
        }}
        required
        disabled={type === 'edit'}
      />

      <TextInputField
        name="name"
        autocompleteKey="budgetType.name"
        label={t('budget-type.master-table-title-budget-name')}
        control={control}
        errors={errors}
        required
        disabled={budgetType === 'initial'}
      />

      {!isRS() && (
        <>
          <TextInputField
            name="externalSystemKey"
            autocompleteKey="budgetType.externalSystemKey"
            label={t('budget-type-external-system-key')}
            control={control}
            errors={errors}
            maxLength={BUDGET_TYPE_EXTERNAL_SYSTEM_KEY_MAX_LENGTH}
            fallbackValue={null}
          />

          <DatePickerField
            name="scheduledDateOfDecision"
            label={t('scheduled-date-of-decision')}
            control={control}
            errors={errors}
          />
        </>
      )}

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
